'use client'

import Link from 'next/link'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'
import { EASE, Eyebrow, HAIRLINE, useReveal } from '@/components/page-kit'
import { COMPLIANCE, POLICIES, RIGHTS, STANDARDS } from '@/config/compliance'

/* Compliance's device: THE CONTROL SHEET.

   A compliance hub is a register of binding documents, so the page is set as a
   document control sheet: a § reference in a left margin, the entry in the
   middle, and its effective date or meta in a right margin. Content sits
   between two gutters.

   Distinct from what is already in use: Payroll's statement carries amounts to
   the far right with dotted leaders and has no left gutter; Accounts' T-account
   is one centre rule with content balanced across it; LMS's margin rule is a
   single vertical line with ordinals beside it; customer stories' tab rule is
   horizontal and broken by its own label. Here nothing is drawn at all — the
   structure is two margins, and the § reference is the only mark.

   Everything the old page did decoratively is gone: the spinning dashed rings,
   the orbiting dots, the corner brackets, the glowing shadow dot, the
   icon-in-a-tinted-rounded-square, the hover-lift cards and the corner blob. */

export function CompliancePage() {
  return (
    <main className="reveal-scope relative bg-background">
      <ComplianceHead />

      <Sheet
        reference="01"
        title={COMPLIANCE.policiesTitle}
        deck={COMPLIANCE.policiesDeck}
        meta={COMPLIANCE.policiesNote}
      >
        {POLICIES.map((p, i) => (
          <Row
            key={p.title}
            index={i}
            reference={`01.${i + 1}`}
            meta={p.badge}
            href={p.href}
            title={p.title}
            body={p.description}
          />
        ))}
      </Sheet>

      <Sheet
        reference="02"
        title={COMPLIANCE.standardsTitle}
        deck={COMPLIANCE.standardsDeck}
      >
        {STANDARDS.map((s, i) => (
          <Row
            key={s.title}
            index={i}
            reference={`02.${i + 1}`}
            title={s.title}
            body={s.description}
          />
        ))}
      </Sheet>

      <Sheet
        reference="03"
        title={COMPLIANCE.rightsTitle}
        deck={COMPLIANCE.rightsDeck}
      >
        {RIGHTS.map((r, i) => (
          <Row
            key={r.right}
            index={i}
            reference={`03.${i + 1}`}
            title={r.right}
            body={r.detail}
            compact
          />
        ))}

        {/* Qualifies every right above — substantive, not decoration. */}
        <p className="max-w-3xl py-6 text-sm leading-relaxed text-muted-foreground">
          {COMPLIANCE.rightsNoteBefore}
          <a
            href={`mailto:${COMPLIANCE.contactEmail}`}
            className="text-emerald-600 underline-offset-4 hover:underline dark:text-primary-active"
          >
            {COMPLIANCE.contactEmail}
          </a>
          {COMPLIANCE.rightsNoteAfter}
        </p>
      </Sheet>

      <ComplianceContact />
    </main>
  )
}

/* ─── Head ─────────────────────────────────────────────────────────────── */
function ComplianceHead() {
  return (
    <section
      className={cn(
        'relative overflow-hidden hero-adaptive-bg',
        'mt-[calc(var(--header-h)*-1)]'
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage: 'var(--circuit-bg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage:
            'radial-gradient(ellipse 110% 68% at 50% 26%, #000 18%, rgba(0,0,0,0.45) 60%, transparent 94%)',
          maskImage:
            'radial-gradient(ellipse 110% 68% at 50% 26%, #000 18%, rgba(0,0,0,0.45) 60%, transparent 94%)',
        }}
      />

      <div className="container relative z-10 mx-auto">
        <div className="flex min-h-[58svh] flex-col justify-center pb-14 pt-[calc(var(--header-h)+4rem)] sm:pb-20">
          <Eyebrow>{COMPLIANCE.badge}</Eyebrow>

          <h1 className="font-heading mt-6 text-[36px]/[1.1] font-normal tracking-tight text-foreground sm:text-5xl/[1.1] md:text-6xl/[1.08] lg:text-[68px]/[1.06]">
            <ChromaText>{COMPLIANCE.title}</ChromaText>
          </h1>

          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#2B3A46] sm:text-lg dark:text-[#DEE9EE]/90">
            {COMPLIANCE.deck}
          </p>

          <div
            className={cn(
              'mt-8 flex flex-wrap items-center gap-x-3 gap-y-1 border-t pt-5 text-sm text-muted-foreground',
              HAIRLINE
            )}
          >
            <span>{COMPLIANCE.entity}</span>
            <span aria-hidden>·</span>
            <span>{COMPLIANCE.jurisdiction}</span>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── One numbered sheet ───────────────────────────────────────────────── */
function Sheet({
  reference,
  title,
  deck,
  meta,
  children,
}: {
  reference: string
  title: string
  deck: string
  meta?: string
  children: React.ReactNode
}) {
  return (
    <section className="pt-16 sm:pt-20 md:pt-24">
      <div className="container mx-auto">
        <div className="grid grid-cols-[2.5rem_1fr] gap-x-4 sm:grid-cols-[4rem_1fr] sm:gap-x-8">
          <span className="font-heading pt-1 text-[13px] font-normal tabular-nums text-emerald-600 dark:text-primary-active">
            §{reference}
          </span>

          <div className="min-w-0">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8">
              <h2 className="font-heading text-2xl font-normal leading-tight text-foreground sm:text-3xl md:text-4xl">
                <ChromaText>{title}</ChromaText>
              </h2>
              {meta && (
                <span className="shrink-0 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  {meta}
                </span>
              )}
            </div>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
              {deck}
            </p>
          </div>
        </div>

        <div className={cn('mt-8 border-t sm:mt-10', HAIRLINE)}>{children}</div>
      </div>
    </section>
  )
}

/* ─── One register row ─────────────────────────────────────────────────── */
function Row({
  reference,
  title,
  body,
  meta,
  href,
  index,
  compact,
}: {
  reference: string
  title: string
  body: string
  meta?: string
  href?: string
  index: number
  compact?: boolean
}) {
  const row = useReveal<HTMLDivElement>(0.15)

  const inner = (
    <>
      {/* Left gutter: the clause reference. */}
      <span className="pt-0.5 text-[11px] tabular-nums text-muted-foreground/60">
        {reference}
      </span>

      <div className="min-w-0">
        <h3
          className={cn(
            'font-heading font-normal leading-snug text-foreground',
            compact ? 'text-base sm:text-lg' : 'text-lg sm:text-xl'
          )}
        >
          {title}
        </h3>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground sm:text-base">
          {body}
        </p>
      </div>

      {/* Right gutter: effective date, and for a document the read affordance
          underneath it. "Read full policy" is authored copy — it names what the
          row does, so it stays visible rather than being reduced to a chevron. */}
      <span className="col-start-2 mt-3 flex flex-col gap-1 sm:col-start-3 sm:mt-0 sm:items-end">
        {meta && (
          <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            {meta}
          </span>
        )}
        {href && (
          <span className="flex items-center gap-1.5 text-[13px] text-emerald-600 dark:text-primary-active">
            Read full policy
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
              className="size-3.5 shrink-0 transition-transform duration-300 group-hover:translate-x-1"
            >
              <path
                d="M6 4l8 8-8 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        )}
      </span>
    </>
  )

  const shell = cn(
    'grid grid-cols-[2.5rem_1fr] gap-x-4 border-b py-5 sm:grid-cols-[4rem_1fr_minmax(0,9rem)] sm:gap-x-8 sm:py-6',
    HAIRLINE
  )

  const style = {
    opacity: row.shown ? 1 : 0,
    transform: row.shown ? 'none' : 'translateY(8px)',
    transition: `opacity 480ms ${EASE} ${index * 70}ms, transform 480ms ${EASE} ${index * 70}ms`,
  }

  if (href) {
    return (
      <Link
        ref={row.ref as React.Ref<HTMLAnchorElement>}
        href={href}
        className={cn(
          shell,
          'group transition-colors hover:bg-muted/40 dark:hover:bg-white/[0.02]',
          'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
          'focus-visible:outline-emerald-600 dark:focus-visible:outline-primary-active'
        )}
        style={style}
      >
        {inner}
      </Link>
    )
  }

  return (
    <div ref={row.ref} className={shell} style={style}>
      {inner}
    </div>
  )
}

/* ─── Contact + notice ─────────────────────────────────────────────────── */
function ComplianceContact() {
  const block = useReveal<HTMLDivElement>(0.2)

  return (
    <section className="relative mt-16 overflow-hidden sm:mt-20 md:mt-24">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[80%]"
        style={{
          backgroundImage: 'var(--circuit-bg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage:
            'radial-gradient(ellipse 110% 76% at 50% 34%, #000 16%, rgba(0,0,0,0.4) 58%, transparent 92%)',
          maskImage:
            'radial-gradient(ellipse 110% 76% at 50% 34%, #000 16%, rgba(0,0,0,0.4) 58%, transparent 92%)',
        }}
      />

      <div
        ref={block.ref}
        className="container relative z-10 mx-auto py-16 sm:py-20"
      >
        <div className="grid grid-cols-[2.5rem_1fr] gap-x-4 sm:grid-cols-[4rem_1fr] sm:gap-x-8">
          <span className="font-heading pt-1 text-[13px] font-normal tabular-nums text-emerald-600 dark:text-primary-active">
            §04
          </span>

          <div className="min-w-0">
            <h2 className="font-heading text-2xl font-normal leading-tight text-foreground sm:text-3xl md:text-4xl">
              <ChromaText>{COMPLIANCE.contactTitle}</ChromaText>
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
              {COMPLIANCE.contactDeck}
            </p>

            <div className="mt-8">
              <Button asChild size="xl">
                <a href={`mailto:${COMPLIANCE.contactEmail}`}>
                  {COMPLIANCE.contactEmail}
                </a>
              </Button>
            </div>

            <address className={cn('mt-10 border-t pt-6 not-italic', HAIRLINE)}>
              <p className="text-sm text-foreground">{COMPLIANCE.entity}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {COMPLIANCE.address}
              </p>
            </address>

            <div className={cn('mt-8 border-t pt-6', HAIRLINE)}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                {COMPLIANCE.noticeLabel}
              </p>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
                {COMPLIANCE.notice}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
